import React, { useState } from "react";
import styles from "../styles/Main.module.css";
import { usePayslipForm } from "../payroll/usePayslipForm";
import { generatePayslipPdf } from "../payroll/generatePayslipPdf";
import { fileToDataUrl } from "../utils/fileToDataUrl";

export default function Main() {
  const {
    formData,
    updateField,
    updateLine,
    addLine,
    removeLine,
    updateNotes,
    resetForm,
    grossEarnings,
    calculation,
  } = usePayslipForm();

  const [logoFile, setLogoFile] = useState(null); // intentionally not persisted, see note above

  const handleGeneratePayslip = async (e) => {
  e.preventDefault();

  let logoDataUrl = null;
  if (logoFile) {
    try {
      logoDataUrl = await fileToDataUrl(logoFile);
    } catch (err) {
      console.warn("Could not read logo file:", err);
    }
  }

  generatePayslipPdf({ formData, calculation, grossEarnings, logoDataUrl });
};

  return (
    <section className={styles.main} id="payslip-form">
      <div className={styles.container}>
        <div className={styles.headerBlock}>
          <span className={styles.eyebrow}>Payslip generator</span>
          <h2 className={styles.heading}>Enter your payslip details</h2>
          <p className={styles.subheading}>
            Fill in the fields below. Everything is calculated automatically
            and your progress is saved on this device as you go.
          </p>
        </div>

        <form className={styles.form} onSubmit={handleGeneratePayslip}>
          {/* Company branding */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Company logo</h3>
            <p className={styles.cardHint}>Optional. 240×240px, max 1MB. Not saved between sessions.</p>
            <div className={styles.uploadBox}>
              <input
                type="file"
                id="logo"
                accept="image/*"
                className={styles.fileInput}
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
              />
              <label htmlFor="logo" className={styles.uploadLabel}>
                <span className={styles.uploadIcon}>+</span>
                {logoFile ? logoFile.name : "Upload logo"}
              </label>
            </div>
          </div>

          {/* Employer details */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Employer details</h3>
            <div className={styles.grid2}>
              <div className={styles.field}>
                <label>Employer name</label>
                <input
                  type="text"
                  placeholder="e.g. Fegaropetra Ltd"
                  value={formData.employer.name}
                  onChange={(e) => updateField("employer", "name", e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label>Employer tax ID</label>
                <input
                  type="text"
                  placeholder="e.g. 12345678X"
                  value={formData.employer.taxId}
                  onChange={(e) => updateField("employer", "taxId", e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label>Employment type</label>
                <select
                  value={formData.employer.employmentType}
                  onChange={(e) => updateField("employer", "employmentType", e.target.value)}
                >
                  <option value="" disabled>Select type</option>
                  <option value="Full-Time">Full-time</option>
                  <option value="Part-Time">Part-time</option>
                </select>
              </div>
              <div className={styles.field}>
                <label>Currency</label>
                <select
                  value={formData.employer.currency}
                  onChange={(e) => updateField("employer", "currency", e.target.value)}
                >
                  <option value="EUR">EUR - €</option>
                  <option value="USD">USD - $</option>
                  <option value="GBP">GBP - £</option>
                </select>
              </div>
            </div>
          </div>

          {/* Employee details */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Employee details</h3>
            <div className={styles.grid2}>
              <div className={styles.field}>
                <label>Employee name</label>
                <input
                  type="text"
                  placeholder="e.g. Andreas Georgiou"
                  value={formData.employee.name}
                  onChange={(e) => updateField("employee", "name", e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label>Employee ID</label>
                <input
                  type="text"
                  placeholder="e.g. CY12345678X"
                  value={formData.employee.employeeId}
                  onChange={(e) => updateField("employee", "employeeId", e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label>Social insurance number</label>
                <input
                  type="text"
                  placeholder="e.g. 123-45-6789"
                  value={formData.employee.siNumber}
                  onChange={(e) => updateField("employee", "siNumber", e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label>Position</label>
                <input
                  type="text"
                  placeholder="e.g. Senior Accountant"
                  value={formData.employee.position}
                  onChange={(e) => updateField("employee", "position", e.target.value)}
                />
              </div>
              <div className={`${styles.field} ${styles.spanFull}`}>
                <label>Payment Method</label>

                <select
                  value={formData.employee.paymentMethod}
                  onChange={(e) =>
                    updateField("employee", "paymentMethod", e.target.value)
                  }
                >
                  <option value="">Select payment method</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>
            </div>
          </div>

          {/* Pay period */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Pay period</h3>
            <div className={styles.grid2}>
              <div className={styles.field}>
                <label>Period start</label>
                <input
                  type="date"
                  value={formData.payPeriod.start}
                  onChange={(e) => updateField("payPeriod", "start", e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label>Period end</label>
                <input
                  type="date"
                  value={formData.payPeriod.end}
                  onChange={(e) => updateField("payPeriod", "end", e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label>Payment date</label>
                <input
                  type="date"
                  value={formData.payPeriod.paymentDate}
                  onChange={(e) => updateField("payPeriod", "paymentDate", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Earnings */}
          <div className={styles.card}>
            <div className={styles.cardHeaderRow}>
              <h3 className={styles.cardTitle}>Earnings</h3>
              <button type="button" className={styles.addBtn} onClick={() => addLine("earnings", "New earning")}>
                + Add earning
              </button>
            </div>
            <div className={styles.lineRows}>
              {formData.earnings.map((line) => (
                <div key={line.id} className={styles.lineRow}>
                  <input
                    type="text"
                    className={styles.lineLabel}
                    value={line.label}
                    onChange={(e) => updateLine("earnings", line.id, "label", e.target.value)}
                  />
                  <input
                    type="number"
                    placeholder="0.00"
                    className={styles.lineAmount}
                    value={line.amount}
                    onChange={(e) => updateLine("earnings", line.id, "amount", e.target.value)}
                  />
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => removeLine("earnings", line.id)}
                    aria-label="Remove earning"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className={styles.totalRow}>
              <span>Gross earnings</span>
              <span className={styles.totalAmount}>{grossEarnings.toFixed(2)}</span>
            </div>
          </div>

          {/* Statutory deductions — computed, not editable */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Statutory deductions</h3>
            <p className={styles.cardHint}>
              Calculated automatically from gross earnings using current Cyprus rates.
            </p>
            <div className={styles.lineRows}>
              <div className={styles.staticRow}>
                <span>Income tax</span>
                <span>{calculation.incomeTax.toFixed(2)}</span>
              </div>
              <div className={styles.staticRow}>
                <span>Social insurance (8.8%)</span>
                <span>{calculation.socialInsurance.employee.toFixed(2)}</span>
              </div>
              <div className={styles.staticRow}>
                <span>GESY / GHS contribution (2.65%)</span>
                <span>{calculation.gesy.employee.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Other deductions — manual */}
          <div className={styles.card}>
            <div className={styles.cardHeaderRow}>
              <h3 className={styles.cardTitle}>Other deductions</h3>
              <button
                type="button"
                className={styles.addBtn}
                onClick={() => addLine("otherDeductions", "New deduction")}
              >
                + Add deduction
              </button>
            </div>
            <div className={styles.lineRows}>
              {formData.otherDeductions.map((line) => (
                <div key={line.id} className={styles.lineRow}>
                  <input
                    type="text"
                    className={styles.lineLabel}
                    value={line.label}
                    onChange={(e) => updateLine("otherDeductions", line.id, "label", e.target.value)}
                  />
                  <input
                    type="number"
                    placeholder="0.00"
                    className={styles.lineAmount}
                    value={line.amount}
                    onChange={(e) => updateLine("otherDeductions", line.id, "amount", e.target.value)}
                  />
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => removeLine("otherDeductions", line.id)}
                    aria-label="Remove deduction"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div className={styles.totalRow}>
              <span>Total deductions</span>
              <span className={styles.totalAmount}>{calculation.totalDeductions.toFixed(2)}</span>
            </div>
          </div>

          {/* Employer contributions — computed, not editable */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Employer contributions</h3>
            <p className={styles.cardHint}>
              Paid by the employer, shown for reference — not deducted from net pay.
            </p>
            <div className={styles.lineRows}>
              <div className={styles.staticRow}>
                <span>Social insurance (8.8%)</span>
                <span>{calculation.socialInsurance.employer.toFixed(2)}</span>
              </div>
              <div className={styles.staticRow}>
                <span>GESY contribution (2.9%)</span>
                <span>{calculation.gesy.employer.toFixed(2)}</span>
              </div>
              <div className={styles.staticRow}>
                <span>Redundancy fund (1.2%)</span>
                <span>{calculation.employerExtras.redundancyFund.toFixed(2)}</span>
              </div>
              <div className={styles.staticRow}>
                <span>Human resource development (0.5%)</span>
                <span>{calculation.employerExtras.humanResourceDevelopment.toFixed(2)}</span>
              </div>
              <div className={styles.staticRow}>
                <span>Social cohesion fund (2%)</span>
                <span>{calculation.employerExtras.socialCohesionFund.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Net pay summary */}
          <div className={`${styles.card} ${styles.netCard}`}>
            <span className={styles.netLabel}>Net payable</span>
            <span className={styles.netAmount}>
              {formData.employer.currency === "EUR" ? "€" : formData.employer.currency === "USD" ? "$" : "£"}
              {calculation.netPay.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </span>
            <span className={styles.netHint}>Gross earnings − income tax − social insurance − GESY − other deductions</span>
          </div>

          {/* Notes */}
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Notes</h3>
            <textarea
              rows="3"
              placeholder="Any additional notes to include on the payslip..."
              className={styles.textarea}
              value={formData.notes}
              onChange={(e) => updateNotes(e.target.value)}
            />
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.secondaryAction} onClick={resetForm}>
              Reset form
            </button>
            <button type="submit" className={styles.primaryAction}>
              Generate payslip
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}